/**
 * Helpers du lien public client.
 *
 * L'URL a la forme `/p/<slug>-<token>` :
 * - `slug` est choisi par un admin (lisible, donc devinable),
 * - `token` fait exactement 8 caractères base64url et constitue le SEUL secret.
 *
 * Le lien donne accès au dashboard client réel (montants budgétaires compris).
 * Le navigateur interroge directement la base : il n'y a donc aucune limite de
 * débit possible en amont, et la longueur du token porte seule la sécurité —
 * 64^8 ≈ 2,8 x 10^14 combinaisons. Ne jamais raccourcir ce token.
 * Voir docs/superpowers/specs/2026-08-19-page-publique-client-design.md
 */

/** 64 caractères exactement : 256 % 64 === 0, donc tirage uniforme sans biais */
const TOKEN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export const PUBLIC_PAGE_TOKEN_LENGTH = 8;

/**
 * Domaine sur lequel le lien client est construit.
 *
 * Volontairement distinct de `VITE_APP_URL`, qui vaut `localhost` en
 * développement — c'est ce qu'on veut pour les liens de réinitialisation de
 * mot de passe, qui doivent revenir sur la machine du développeur. Le lien
 * client a le besoin opposé : il est destiné à être envoyé à un vrai client,
 * il doit donc TOUJOURS pointer sur la production, même copié depuis un poste
 * de développement.
 */
export const PUBLIC_LINK_BASE_URL =
  import.meta.env.VITE_PUBLIC_LINK_BASE_URL || 'https://monitoring.asteroideproduction.fr';

/** Longueur minimale du slug, alignée sur la contrainte CHECK en base */
export const PUBLIC_PAGE_SLUG_MIN = 3;
export const PUBLIC_PAGE_SLUG_MAX = 60;

/**
 * Génère un token aléatoire cryptographiquement sûr.
 */
export function generatePublicPageToken(): string {
  const bytes = new Uint8Array(PUBLIC_PAGE_TOKEN_LENGTH);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => TOKEN_ALPHABET[byte % TOKEN_ALPHABET.length]).join('');
}

/**
 * Transforme un nom d'entreprise en slug proposé par défaut.
 * L'admin reste libre de le modifier.
 */
export function slugifyCompanyName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, PUBLIC_PAGE_SLUG_MAX)
    .replace(/-+$/, '');
}

/** `ocean-spray` + `k3Xf` → `ocean-spray-k3Xf` */
export function buildPublicPageHandle(slug: string, token: string): string {
  return `${slug}-${token}`;
}

/**
 * Découpe `<slug>-<token>`.
 *
 * Le token ayant une longueur fixe, le découpage est fait par la fin : le slug
 * peut donc contenir autant de tirets qu'il veut sans ambiguïté.
 * Retourne null si la forme ne colle pas.
 */
export function parsePublicPageHandle(handle: string): { slug: string; token: string } | null {
  const minLength = PUBLIC_PAGE_SLUG_MIN + 1 + PUBLIC_PAGE_TOKEN_LENGTH;
  if (handle.length < minLength) return null;

  const separatorIndex = handle.length - PUBLIC_PAGE_TOKEN_LENGTH - 1;
  if (handle[separatorIndex] !== '-') return null;

  const slug = handle.slice(0, separatorIndex);
  const token = handle.slice(separatorIndex + 1);
  if (!slug || token.length !== PUBLIC_PAGE_TOKEN_LENGTH) return null;

  return { slug, token };
}

/**
 * URL complète à copier et transmettre au client.
 *
 * Toujours construite sur le domaine de production : le bouton « Copier »
 * doit produire un lien envoyable tel quel, y compris depuis un environnement
 * de développement.
 */
export function buildPublicPageUrl(slug: string, token: string, baseUrl?: string): string {
  const base = (baseUrl ?? PUBLIC_LINK_BASE_URL).replace(/\/+$/, '');
  return `${base}/p/${buildPublicPageHandle(slug, token)}`;
}
